import { Link } from 'react-router-dom';
import { useCurrentTenant } from '@/lib/api/tenants';
import { useAgents } from '@/lib/api/agents';
import { useUsage } from '@/lib/api/usage';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useWorkspacePath } from '@/lib/workspacePath';

function formatNumber(value: number | undefined): string {
  if (value === undefined) return '-';
  return value.toLocaleString();
}

export function DashboardPage() {
  const tenant = useCurrentTenant();
  const agents = useAgents();
  const usage = useUsage();
  const ws = useWorkspacePath();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          {tenant.data ? (
            <>
              {tenant.data.name} - plan: {tenant.data.plan_code}
            </>
          ) : tenant.isLoading ? (
            'Loading workspace...'
          ) : (
            'Workspace overview'
          )}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card title="Agents">
          <div className="text-3xl font-semibold text-gray-900">
            {agents.isLoading ? <Skeleton className="h-9 w-16" /> : formatNumber(agents.data?.length)}
          </div>
          <Link to={ws('agents')} className="mt-2 inline-block text-sm text-brand-600 hover:underline">
            Manage agents
          </Link>
        </Card>

        <Card title="Input tokens (period)">
          <div className="text-3xl font-semibold text-gray-900">
            {usage.isLoading ? (
              <Skeleton className="h-9 w-24" />
            ) : (
              formatNumber(usage.data?.input_tokens)
            )}
          </div>
          <p className="mt-2 text-sm text-gray-500">{usage.data?.period ?? 'Current period'}</p>
        </Card>

        <Card title="Output tokens (period)">
          <div className="text-3xl font-semibold text-gray-900">
            {usage.isLoading ? (
              <Skeleton className="h-9 w-24" />
            ) : (
              formatNumber(usage.data?.output_tokens)
            )}
          </div>
          <Link
            to={ws('settings', 'billing')}
            className="mt-2 inline-block text-sm text-brand-600 hover:underline"
          >
            View billing
          </Link>
        </Card>
      </div>

      <Card title="Getting started">
        <ol className="list-inside list-decimal space-y-1 text-sm text-gray-600">
          <li>Create an agent with a system prompt, provider, and model.</li>
          <li>Upload knowledge documents to ground responses.</li>
          <li>Open an agent and start a streaming chat.</li>
          <li>Invite teammates and review token usage in Billing.</li>
        </ol>
      </Card>
    </div>
  );
}
